import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Stamp, Plus, PencilSimple, Trash, MagnifyingGlass, ArrowUp, ArrowDown, ArrowsDownUp, Export, Paperclip, FileArrowUp } from "@phosphor-icons/react";
import { useTableFeatures, exportCSV } from "@/hooks/useTableFeatures";

const defaultForm = { client_id: "", client_name: "", status: "draft", issue_date: "", expiration_date: "", notes: "" };
const statusColors = {
  draft: "bg-gray-100 text-[#6B7280] border-gray-200",
  sent: "bg-[#002FA7]/10 text-[#002FA7] border-[#002FA7]/20",
  signed: "bg-[#00B341]/10 text-[#00B341] border-[#00B341]/20",
  expired: "bg-[#FF2A00]/10 text-[#FF2A00] border-[#FF2A00]/20",
};

export default function LOA() {
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ ...defaultForm });

  const { search, setSearch, sortKey, sortDir, toggleSort, filters, setFilter, clearFilters, processed, activeFilterCount } = useTableFeatures(items);
  const csvCols = [
    { label: "Client", key: "client_name" }, { label: "Status", key: "status" },
    { label: "Issue Date", key: "issue_date" }, { label: "Expiration", key: "expiration_date" }, { label: "Notes", key: "notes" },
  ];
  const SH = ({ k, children }) => (
    <TableHead className="py-3 px-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold cursor-pointer select-none hover:text-[#1A1D20]" onClick={() => toggleSort(k)}>
      <div className="flex items-center gap-1">{children}{sortKey === k ? (sortDir === "asc" ? <ArrowUp size={10} /> : <ArrowDown size={10} />) : <ArrowsDownUp size={10} className="opacity-30" />}</div>
    </TableHead>
  );

  useEffect(() => { load(); loadClients(); }, []);

  const load = async () => { try { const { data } = await api.get("/loas"); setItems(data); } catch { toast.error("Failed to load"); } };
  const loadClients = async () => { try { const { data } = await api.get("/clients"); setClients(data); } catch {} };

  const save = async () => {
    try {
      if (editing) { await api.put(`/loas/${editing}`, form); toast.success("Updated"); }
      else { await api.post("/loas", form); toast.success("Created"); }
      setOpen(false); load();
    } catch { toast.error("Failed to save"); }
  };

  const remove = async (id) => { if (!window.confirm("Delete?")) return; try { await api.delete(`/loas/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Failed"); } };
  const edit = (item) => { setForm({ ...item }); setEditing(item.id); setOpen(true); };
  const openNew = () => { setForm({ ...defaultForm }); setEditing(null); setOpen(true); };
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const selectClient = (id) => { const c = clients.find((cl) => cl.id === id); setForm((f) => ({ ...f, client_id: id, client_name: c ? c.name : "" })); };

  const uploadAttachment = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/attachments/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, attachments: [...(f.attachments || []), data] }));
      toast.success(`Attached: ${file.name}`);
    } catch { toast.error("Upload failed"); }
  };
  const removeAttachment = (idx) => setForm((f) => ({ ...f, attachments: (f.attachments || []).filter((_, i) => i !== idx) }));

  return (
    <div className="p-6" data-testid="loa-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#6B7280] mb-1">Management</p>
          <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold text-[#1A1D20]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Letter of Agency</h1>
        </div>
        <button data-testid="add-loa-btn" onClick={openNew} className="bg-[#002FA7] text-white hover:bg-[#002480] rounded-none font-medium px-4 py-2 text-sm flex items-center gap-2 transition-colors">
          <Plus size={16} weight="bold" /> Add LOA
        </button>
      </div>

      <div className="mb-4 flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6B7280]" />
          <Input data-testid="search-loa" placeholder="Search LOAs..." value={search} onChange={(e) => setSearch(e.target.value)} className="rounded-none pl-9" />
        </div>
        <Select value={filters.status || "all"} onValueChange={(v) => setFilter("status", v)}>
          <SelectTrigger data-testid="filter-loa-status" className="rounded-none w-[130px]"><SelectValue placeholder="All Status" /></SelectTrigger>
          <SelectContent className="rounded-none"><SelectItem value="all">All Status</SelectItem><SelectItem value="draft">Draft</SelectItem><SelectItem value="sent">Sent</SelectItem><SelectItem value="signed">Signed</SelectItem><SelectItem value="expired">Expired</SelectItem></SelectContent>
        </Select>
        {activeFilterCount > 0 && <button onClick={clearFilters} className="text-xs text-[#002FA7] hover:underline">Clear</button>}
        <button data-testid="export-loa-btn" onClick={() => exportCSV(processed, csvCols, "loa.csv")} className="ml-auto flex items-center gap-1.5 bg-transparent text-[#1A1D20] border border-[#1A1D20] hover:bg-[#F8F9FA] rounded-none font-medium px-3 py-2 text-xs transition-colors">
          <Export size={14} /> Export CSV
        </button>
      </div>

      <div className="border border-gray-200 bg-white">
        <Table>
          <TableHeader>
            <TableRow className="data-table-header">
              <SH k="client_name">Client</SH>
              <SH k="status">Status</SH>
              <SH k="issue_date">Issue Date</SH>
              <SH k="expiration_date">Expiration</SH>
              <TableHead className="py-3 px-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold">Notes</TableHead>
              <TableHead className="py-3 px-4 text-xs uppercase tracking-wider text-[#6B7280] font-semibold w-[80px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {processed.length === 0 ? (
              <TableRow><TableCell colSpan={6} className="text-center py-8 text-[#6B7280] text-sm">No LOAs found</TableCell></TableRow>
            ) : processed.map((item) => (
              <TableRow key={item.id} className="border-b border-gray-100 hover:bg-gray-50">
                <TableCell className="py-3 px-4 text-[#1A1D20] font-medium">{item.client_name || "—"}</TableCell>
                <TableCell className="py-3 px-4"><Badge className={`rounded-none text-xs border capitalize ${statusColors[item.status] || statusColors.draft}`}>{item.status}</Badge></TableCell>
                <TableCell className="py-3 px-4 text-sm text-[#1A1D20]">{item.issue_date || "—"}</TableCell>
                <TableCell className="py-3 px-4 text-sm text-[#1A1D20]">{item.expiration_date || "—"}</TableCell>
                <TableCell className="py-3 px-4 text-sm text-[#6B7280] max-w-[200px] truncate">{item.notes || "—"}</TableCell>
                <TableCell className="py-3 px-4">
                  <div className="flex items-center gap-1">
                    <button data-testid={`edit-loa-${item.id}`} onClick={() => edit(item)} className="p-1.5 hover:bg-gray-100"><PencilSimple size={14} className="text-[#6B7280]" /></button>
                    <button data-testid={`delete-loa-${item.id}`} onClick={() => remove(item.id)} className="p-1.5 hover:bg-red-50"><Trash size={14} className="text-[#FF2A00]" /></button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none sm:max-w-[500px]">
          <DialogHeader><DialogTitle style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>{editing ? "Edit" : "Add"} Letter of Agency</DialogTitle></DialogHeader>
          <div className="grid gap-4 py-4">
            <div>
              <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Client</label>
              <Select value={form.client_id} onValueChange={selectClient}>
                <SelectTrigger data-testid="loa-client-select" className="rounded-none"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent className="rounded-none max-h-[200px]">{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Status</label>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger data-testid="loa-status-select" className="rounded-none"><SelectValue /></SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="draft">Draft</SelectItem><SelectItem value="sent">Sent</SelectItem><SelectItem value="signed">Signed</SelectItem><SelectItem value="expired">Expired</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Issue Date</label>
                <Input data-testid="loa-issue-input" type="date" value={form.issue_date} onChange={(e) => set("issue_date", e.target.value)} className="rounded-none" />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Expiration Date</label>
                <Input data-testid="loa-expiration-input" type="date" value={form.expiration_date} onChange={(e) => set("expiration_date", e.target.value)} className="rounded-none" />
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Notes</label>
              <textarea data-testid="loa-notes-input" value={form.notes} onChange={(e) => set("notes", e.target.value)} className="w-full border border-gray-300 rounded-none px-3 py-2 text-sm focus:outline-none focus:border-[#002FA7] focus:ring-1 focus:ring-[#002FA7] min-h-[60px]" />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#6B7280] uppercase tracking-wider mb-1 block">Attachments</label>
              <div className="space-y-2">
                {(form.attachments || []).map((att, i) => (
                  <div key={i} className="flex items-center justify-between border border-gray-200 px-3 py-2 text-sm">
                    <div className="flex items-center gap-2"><Paperclip size={14} className="text-[#002FA7]" /><span>{att.original_filename}</span></div>
                    <button type="button" onClick={() => removeAttachment(i)} className="text-[#FF2A00] text-xs hover:underline">Remove</button>
                  </div>
                ))}
                <label className="flex items-center gap-2 cursor-pointer border border-dashed border-gray-300 px-3 py-2 text-sm text-[#6B7280] hover:border-[#002FA7]/50 transition-colors">
                  <FileArrowUp size={16} /> Upload LOA document
                  <input type="file" className="hidden" onChange={uploadAttachment} data-testid="loa-file-upload" />
                </label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <button data-testid="cancel-loa-btn" onClick={() => setOpen(false)} className="bg-transparent text-[#1A1D20] border border-[#1A1D20] hover:bg-[#F8F9FA] rounded-none font-medium px-4 py-2 text-sm">Cancel</button>
            <button data-testid="save-loa-btn" onClick={save} className="bg-[#002FA7] text-white hover:bg-[#002480] rounded-none font-medium px-4 py-2 text-sm">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
